import React from "react";
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, Outlet } from "react-router";
import { bool, node, string } from "prop-types";
import Box from "@mui/material/Box";

import StateProvider from "./StateProvider";
import I18nProvider from "shared/i18n/I18nProvider";

// Layout Component
import Header from "./navigation/Header";
import Footer from "shared/chrome/Footer";
import PlatformAlerts from "shared/chrome/PlatformAlerts";

// Pages
import AppLanding from "./AppPages/AppLanding";
import ComingSoon from "./AppPages/ComingSoon";
import DocumentsPage from "./AppPages/DocumentsPage";
import Landing from "./landing/Landing";
import Updates from "./AppPages/Updates";
import VideosPage from "./AppPages/VideosPage";
import Webinars from "./AppPages/Webinars";
// Tool Related Pages
import TabNavigator from "./TabNavigator";
import Demo from "./Demo";
// User Related Pages
import ChangePassword from "./AppPages/ChangePassword";
import ChangeAccountInfo from "./AppPages/ChangeAccountInfo";
import AccessibilityChecker from "./AccessibilityChecker";
import StateInitializer from "./StateInitializer";

// Layout component for authenticated routes
const AuthenticatedLayout = () => (
  <div id="spa_body">
    <StateInitializer>
      <Box style={{ width: "100%" }}>
        <PlatformAlerts />
      </Box>
      <Outlet />
    </StateInitializer>
  </div>
);

// Layout component for unauthenticated routes
const UnauthenticatedLayout = () => (
  <div id="spa_body">
    <Outlet />
  </div>
);

// Layout component for demo route (no header, no footer from shared layout)
const DemoLayout = () => (
  <div id="spa_body">
    <Outlet />
  </div>
);

// Component that conditionally renders based on auth status
const ConditionalLayout = ({ isSignedIn }) => {
  if (!isSignedIn) {
    return <UnauthenticatedLayout />;
  }
  return <AuthenticatedLayout />;
};

ConditionalLayout.propTypes = {
  isSignedIn: bool.isRequired,
};

// Protected route element wrapper
const ProtectedElement = ({ element, isSignedIn, redirectTo = "/" }) => {
  if (!isSignedIn) {
    return <Navigate to={redirectTo} replace />;
  }
  return element;
};

ProtectedElement.propTypes = {
  element: node.isRequired,
  isSignedIn: bool.isRequired,
  redirectTo: string,
};

// Public route element wrapper (redirects if signed in)
const PublicElement = ({ element, isSignedIn, redirectTo = "/tools" }) => {
  if (isSignedIn) {
    return <Navigate to={redirectTo} replace />;
  }
  return element;
};

PublicElement.propTypes = {
  element: node.isRequired,
  isSignedIn: bool.isRequired,
  redirectTo: string,
};

// Empty component for Devise views - Rails handles the content
const DeviseContent = () => null;

// Wrapper component that applies Header to all routes
const AppWithHeader = ({ isSignedIn, isDeviseView }) => {
  // For Devise views (Rails-handled routes), only render Header and Footer
  // Render a catch-all route that renders nothing so React Router doesn't interfere
  if (isDeviseView) {
    return (
      <>
        <Header isSignedIn={isSignedIn} isDeviseView={isDeviseView} />
        <Routes>
          <Route path="*" element={<DeviseContent />} />
        </Routes>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header isSignedIn={isSignedIn} isDeviseView={isDeviseView} />
      <Routes>
        {/* Demo route - accessible to all */}
        <Route path="/demo" element={<DemoLayout />}>
          <Route index element={<Demo />} />
        </Route>

        {/* Main routes with conditional layout */}
        <Route path="/*" element={<ConditionalLayout isSignedIn={isSignedIn} />}>
          {/* Unauthenticated routes - use index for root */}
          <Route
            index
            element={<PublicElement element={<Landing isSignedIn />} isSignedIn={isSignedIn} />}
          />

          {/* Authenticated routes - use relative paths */}
          <Route
            path="password"
            element={<ProtectedElement element={<ChangePassword />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="account"
            element={<ProtectedElement element={<ChangeAccountInfo />} isSignedIn={isSignedIn} />}
          />

          {/* Specific routes before catch-all tab navigation */}
          <Route
            path="coming-soon"
            element={<ProtectedElement element={<ComingSoon />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="documents"
            element={<ProtectedElement element={<DocumentsPage />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="tools"
            element={<ProtectedElement element={<AppLanding />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="updates"
            element={<ProtectedElement element={<Updates />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="videos"
            element={<ProtectedElement element={<VideosPage />} isSignedIn={isSignedIn} />}
          />
          <Route
            path="webinars"
            element={<ProtectedElement element={<Webinars />} isSignedIn={isSignedIn} />}
          />

          {/* Tab navigation routes - use parameter but validate in component */}
          <Route
            path=":activeTabKey"
            element={<ProtectedElement element={<TabNavigator />} isSignedIn={isSignedIn} />}
          >
            <Route path=":detailId" element={<TabNavigator />} />
            <Route index element={<TabNavigator />} />
          </Route>

          {/* Catch-all redirect - must be last */}
          <Route path="*" element={<Navigate to={isSignedIn ? "/tools" : "/"} replace />} />
        </Route>
      </Routes>
      <Footer />
    </>
  );
};

AppWithHeader.propTypes = {
  isSignedIn: bool.isRequired,
  isDeviseView: bool.isRequired,
};

const App = ({ isSignedIn, isDeviseView }) => {
  const routerChildren = (
    <AccessibilityChecker>
      <AppWithHeader isSignedIn={isSignedIn} isDeviseView={isDeviseView} />
    </AccessibilityChecker>
  );

  if (isDeviseView) {
    const initialEntry =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search || ""}`
        : "/";

    return (
      <I18nProvider>
        <StateProvider>
          <MemoryRouter initialEntries={[initialEntry]}>{routerChildren}</MemoryRouter>
        </StateProvider>
      </I18nProvider>
    );
  }

  return (
    <I18nProvider>
      <StateProvider>
        <BrowserRouter>{routerChildren}</BrowserRouter>
      </StateProvider>
    </I18nProvider>
  );
};

App.propTypes = {
  isSignedIn: bool.isRequired,
  isDeviseView: bool.isRequired,
};

export default App;
